import { type AnalyticsEvent } from '@backstage/core-plugin-api';
import { type MetricRequest } from '@internal/plugin-neo4j-analytics-common';

import { BaseAnalyticsEventHandler } from './types';
import { NEO4J_SEARCH_EVENT_NAME } from '../const';

export class SearchEventHandler extends BaseAnalyticsEventHandler {
  /**
   * The `search` event `value` (number of results) is not available in
   * deployments due to the permissions framework.
   *
   * Therefore, we need to create our own event name to distinguish it
   * and call `useAnalytics.captureEvent` to capture the event.
   *
   * @see https://backstage.io/docs/plugins/analytics/#key-events
   */
  readonly eventName = NEO4J_SEARCH_EVENT_NAME;
  readonly metricName = 'search';

  canHandle(action: string): boolean {
    return action === this.eventName;
  }

  handle(event: AnalyticsEvent): MetricRequest[] {
    if (!this.canHandle(event.action)) {
      return [];
    }

    const name = this.getMetricName(this.metricName);

    if (event.value === undefined) {
      return [];
    }

    return [
      {
        kind: 'histogram',
        name,
        value: event.value,
        attributes: {
          extension: event.context.extension,
          term: event.subject.trim(),
        },
        options: {
          description: 'Histogram of search result counts',
          unit: '1',
          valueType: 'int',
          advice: {
            explicitBucketBoundaries: [5, 10, 20, 50, 100],
          },
        },
      },
    ];
  }
}
